import * as React from 'react';

import { cn } from '@/lib/utils';

interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: number;
  max?: number;
  variant?: 'determinate' | 'indeterminate' | 'circular';
  /**
   * `inverse` is light ink for a dark surface that stays dark in both themes
   * (the glass popover).
   */
  color?: 'primary' | 'success' | 'inverse';
  indicatorClassName?: string;
  indicatorStyle?: React.CSSProperties;
  size?: number | string;
  strokeWidth?: number;
  label?: React.ReactNode;
  pathColor?: string;
  trailColor?: string;
  textColor?: string;
  pathTransitionDuration?: number;
  sx?: React.CSSProperties;
}

const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  (
    {
      className,
      value = 0,
      max = 100,
      variant = 'determinate',
      color = 'primary',
      indicatorClassName,
      indicatorStyle,
      size,
      strokeWidth = 4,
      label,
      pathColor,
      trailColor,
      textColor,
      pathTransitionDuration = 0.2,
      sx,
      style,
      'aria-valuenow': ariaValueNow,
      ...props
    },
    ref
  ) => {
    const safeMax = max <= 0 ? 100 : max;
    const boundedValue = Math.min(Math.max(value, 0), safeMax);
    const percent = (boundedValue / safeMax) * 100;
    const normalizedStrokeWidth = Math.min(Math.max(strokeWidth, 1), 20);

    // A progressbar must expose an accessible name. Respect any name the
    // consumer provides (aria-label/aria-labelledby/title); otherwise fall
    // back to a sensible default so no progressbar is ever announced as
    // anonymous to assistive tech.
    const hasAccessibleName =
      props['aria-label'] != null ||
      props['aria-labelledby'] != null ||
      props.title != null;

    const fallbackAriaLabel =
      variant === 'indeterminate'
        ? 'Loading'
        : typeof label === 'string' && label.trim().length > 0
          ? label
          : `Progress: ${Math.round(percent)}%`;

    const accessibleNameProps = hasAccessibleName
      ? {}
      : { 'aria-label': fallbackAriaLabel };

    if (variant === 'circular') {
      const radius = 50 - normalizedStrokeWidth / 2;
      const circumference = 2 * Math.PI * radius;
      const dashOffset = circumference * (1 - percent / 100);

      return (
        <div
          ref={ref}
          className={cn(
            'relative inline-flex items-center justify-center',
            'ui-progress-circular-root',
            className
          )}
          style={{
            width: size ?? '100%',
            height: size ?? '100%',
            ...(sx ?? {}),
            ...(style ?? {}),
          }}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={safeMax}
          aria-valuenow={ariaValueNow ?? Math.round(boundedValue)}
          {...props}
          {...accessibleNameProps}
        >
          <svg
            className="CircularProgressbar ui-progress-circular h-full w-full"
            viewBox="0 0 100 100"
          >
            <circle
              className="CircularProgressbar-trail ui-progress-circular-trail"
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke={trailColor ?? 'var(--surface-tertiary)'}
              strokeWidth={normalizedStrokeWidth}
            />
            <circle
              className={cn(
                'CircularProgressbar-path ui-progress-circular-path',
                indicatorClassName
              )}
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke={
                pathColor ??
                (color === 'success'
                  ? 'var(--status-success)'
                  : color === 'inverse'
                    ? 'var(--color-neutral-25)'
                    : 'var(--type-heading)')
              }
              strokeWidth={normalizedStrokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              transform="rotate(-90 50 50)"
              style={{
                transitionProperty: 'stroke-dashoffset',
                transitionDuration: `${pathTransitionDuration}s`,
                transitionTimingFunction: 'ease-out',
              }}
            />
          </svg>
          {label !== undefined && label !== null && (
            <span
              className={cn(
                'CircularProgressbar-text ui-progress-circular-label pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                color === 'success'
                  ? 'text-status-success'
                  : color === 'inverse'
                    ? 'text-neutral-25'
                    : 'text-fg-primary'
              )}
              style={{ color: textColor }}
            >
              {label}
            </span>
          )}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          'relative h-2 w-full overflow-hidden rounded-full',
          color === 'inverse' ? 'bg-neutral-0-10' : 'bg-surface-muted',
          'ui-progress-root',
          className
        )}
        style={{ ...(sx ?? {}), ...(style ?? {}) }}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={safeMax}
        aria-valuenow={
          variant === 'indeterminate'
            ? undefined
            : (ariaValueNow ?? Math.round(boundedValue))
        }
        {...props}
        {...accessibleNameProps}
      >
        <div
          className={cn(
            'h-full transition-[width] duration-200 ease-out',
            'ui-progress-bar',
            color === 'success'
              ? 'bg-status-success'
              : color === 'inverse'
                ? 'bg-neutral-25'
                : 'bg-fg-primary',
            indicatorClassName
          )}
          style={{
            width: variant === 'indeterminate' ? '100%' : `${percent}%`,
            ...(indicatorStyle ?? {}),
          }}
        />
      </div>
    );
  }
);

Progress.displayName = 'Progress';

export { Progress };
export default Progress;
